import {FormGroup} from "@angular/forms";
import _cloneDeep from "lodash/cloneDeep";
import {Observable, Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, map} from "rxjs/operators";

export abstract class ObservableBaseForm<TFormModel extends object> {

    private formChanged = new Subject<TFormModel>();
    private formSubmitted = new Subject<TFormModel>();
    private statusChanged = new Subject<string>();

    private changeSubscription: Subscription;
    private statusSubscription: Subscription;

    public get changeObservable(): Observable<TFormModel> {
        return this.formChanged.pipe(
            distinctUntilChanged()
        );
    }

    public get submitObservable(): Observable<TFormModel> {
        return this.formSubmitted.pipe(
            debounceTime(250),
        );
    }

    public get validityObservable(): Observable<boolean> {
        return this.statusChanged.pipe(
            distinctUntilChanged(),
            map(status => status === "VALID" ? true : false)
        );
    }

    protected constructor(
        public formInstance: FormGroup,
        protected initialValues: TFormModel,
    ) {
        this.addChangeListener();
        this.addStatusListener();

    }

    public submit(): void {
        if (this.validate()) {
            this.formSubmitted.next(this.getModel());
        }
    }

    public getModel = (partialModel: any = {}): TFormModel => {
        return _cloneDeep({
            ...this.formInstance.value,
            ...partialModel
        }) as TFormModel;
    }

    public abstract reset(): void;

    public validate = (): boolean => {
        return this.formInstance.valid;
    }

    public dispose(): void {
        this.formSubmitted.complete();
        this.formChanged.complete();
        this.statusChanged.complete();
        this.removeChangeListener();
        this.removeStatusListener();
    }

    protected addChangeListener = () => {
        this.removeChangeListener();

        const changeSubscription = this.createChangeSubscription();
        if(changeSubscription) {
            this.changeSubscription = changeSubscription.subscribe(this.formChanged);
        }
    }

    private addStatusListener = () => {
        this.statusSubscription = this.formInstance.statusChanges
            .subscribe(this.statusChanged);
    }

    protected abstract createChangeSubscription(): Observable<TFormModel>;

    protected removeChangeListener = () => {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
            this.changeSubscription = null;
        }
    }

    protected removeStatusListener = () => {
        if(this.statusSubscription) {
            this.statusSubscription.unsubscribe();
            this.statusSubscription = null;
        }
    }
}
